import { UIButton, UICheckButton, UIControl, UILabel, UIView, UIViewController, UIViewControllerTransitioningDelegate, _CAAnimationType, _CAClassListForAnimationType, UIApplication } from "uikit";
import { BookingProductCategoryViewController } from "./BookingProductCategoryViewController";
import { BookingProductViewController, BookingProductProtocolDelegate } from "./BookingProductViewController";
import { BookingCalendarViewController, BookingCalendarProtocolDelegate } from "./BookingCalendarViewController";
import { BookingRestaurantViewController, BookingRestaurantProtocolDelegate } from "./BookingRestaurantViewController";
import { BookingClientProtocolDelegate, BookingClientViewController } from "./BookingClientViewController";
import { BookingSummaryViewController } from "./BookingSummaryViewController";
import { NSLocalizeString, NSObject } from "foundation";
import { MIOCoreGetPlatformLanguage, MIOStringWithFormat, MIOCoreGetLanguages } from "mio-core";
import { DLDB, RIGHTS_URL } from "dldb";
import { MUIComboBox } from "muikit";
import { BookingReviewViewController } from "./BookingReviewViewController";
import { BookingPaxViewController } from "./BookingPaxViewController";
import { BookingEventViewController } from "./BookingEventViewController";
import { BookingStep, bookingStepsOf, isBookingStep } from "./StepView";
import { AppDelegate } from "../AppDelegate";


export class BookingViewController extends UIViewController implements BookingCalendarProtocolDelegate, BookingRestaurantProtocolDelegate, BookingClientProtocolDelegate
{
    protected errorLbl            : UILabel         = null ;
    private   payLoading          : UIView          = null ;

    private   languageComboBox    : MUIComboBox     = null ;

    private   acceptTermsCB       : UICheckButton   = null ;
    private   acceptPrivacyCB     : UICheckButton   = null ;
    private   acceptPublicityCB   : UICheckButton   = null ;
    private   acceptTermsLbl      : UILabel         = null ;
    private   acceptPrivacyLbl    : UILabel         = null ;
    private   bookButton          : UIButton        = null ;
    private   reviewButton        : UIButton        = null ;

    protected productCategoryViewController:BookingProductCategoryViewController = null;
    protected productViewController:BookingProductViewController = null;
    protected paxViewController:BookingPaxViewController = null;
    protected eventViewController:BookingEventViewController = null;
    protected calendarViewController:BookingCalendarViewController = null;
    protected restaurantViewController:BookingRestaurantViewController = null;
    protected clientViewController:BookingClientViewController = null;

    // Steps present in this venue's layout, in DOM order (see setupSteps)
    protected stepViewControllers: BookingStep[] = [];

    viewDidLoad ( )
    {
        super.viewDidLoad();            

        if ( this.languageComboBox ) {
            this.languageComboBox.hidden = false ;
            this.languageComboBox.addTarget( this, this.onLanguageChange, UIControl.Event.valueChanged);
            this.setupLanguageComboBox();
        }

        if ( DLDB.shared.combineTermsAndPrivacy ) {
            // One line for both: the single checkbox drives acceptTerms AND acceptPrivacy
            if ( this.acceptTermsLbl )
                this.acceptTermsLbl.text = MIOStringWithFormat( NSLocalizeString( "ACCEPT TERMS AND PRIVACY", "ACCEPT TERMS AND PRIVACY" ), DLDB.shared.getURL( RIGHTS_URL.TERMS ), DLDB.shared.getURL( RIGHTS_URL.PRIVACY ) ) ;
            if ( this.acceptPrivacyCB ) this.acceptPrivacyCB.hidden = true;
            if ( this.acceptPrivacyLbl ) this.acceptPrivacyLbl.hidden = true;
        }
        else {
            if ( this.acceptPrivacyLbl )
                this.acceptPrivacyLbl.text = MIOStringWithFormat( NSLocalizeString( "ACCEPT PRIVACY", "ACCEPT PRIVACY" ), DLDB.shared.getURL( RIGHTS_URL.PRIVACY ) ) ;

            if ( this.acceptTermsLbl )
                this.acceptTermsLbl.text = MIOStringWithFormat( NSLocalizeString( "ACCEPT TERMS", "ACCEPT TERMS" ), DLDB.shared.getURL( RIGHTS_URL.TERMS ) ) ;
        }
        if ( this.acceptTermsCB )
            this.acceptTermsCB.addTarget( this, this.onAcceptedCheck, UIControl.Event.touchUpInside );

        if ( this.acceptPrivacyCB)
            this.acceptPrivacyCB.addTarget( this, this.onAcceptedCheck, UIControl.Event.touchUpInside );

        if ( this.acceptPublicityCB)
            this.acceptPublicityCB.addTarget( this, this.onAcceptedCheck, UIControl.Event.touchUpInside );

        this.errorLbl.setHidden(true);
        this.setLoading(false);

        if (this.bookButton) {
            this.bookButton.addTarget(this, this.onBooking, UIControl.Event.touchUpInside);
            this.bookButton.enabled = false;
        }

        if (this.reviewButton) {
            this.reviewButton.addTarget(this, this.onReview, UIControl.Event.touchUpInside);
            this.reviewButton.enabled = false;
        }
      }
      
    viewDidAppear ( animated: boolean ) {
        super.viewDidAppear(animated);
        this.checkBookBtn();
    }

    /**
     * Collects the booking steps among the child view controllers, orders them as they appear in the
     * layout (DOM order) and numbers their bubbles accordingly. A venue reorders or hides steps by
     * editing its BookingDesktopView/BookingMobileView layout only.
     */
    protected setupSteps ( ) {
        const steps = bookingStepsOf( this.childViewControllers );

        this.stepViewControllers = steps;
        this.renumberSteps();

        // Step layouts are loaded asynchronously (nib XHR): number each bubble again once its view
        // controller has actually loaded, otherwise the hard-coded numbers of the layout would show.
        for ( const step of steps ) {
            const vc = step as unknown as UIViewController;
            const original = vc.viewDidLoad.bind( vc );
            vc.viewDidLoad = () => { original(); this.renumberSteps(); };
        }
    }

    protected renumberSteps ( ) {
        for ( let i = 0 ; i < this.stepViewControllers.length ; i++ ) {
            const stepView = this.stepViewControllers[ i ].stepView;
            if ( stepView == null ) continue;
            stepView.number = i + 1;
        }
        this.highlightCurrentStep();
    }

    /**
     * The highlighted pill is always the first step (in layout order) that still has no value:
     * fill steps 1 and 3 and the highlight sits on 2; fill 2 and it jumps to the next empty one.
     * Recomputed after every change, so undoing a selection moves the highlight back too.
     */
    protected highlightCurrentStep ( ) {
        let current = -1;
        for ( let i = 0 ; i < this.stepViewControllers.length ; i++ ) {
            if ( !this.stepViewControllers[ i ].hasValue() ) { current = i; break; }
        }

        for ( let i = 0 ; i < this.stepViewControllers.length ; i++ ) {
            const stepView = this.stepViewControllers[ i ].stepView;
            if ( stepView != null ) stepView.selected = ( i === current );
        }
    }

    /** Every step present validates; the booking payload itself is assembled from the cart. */
    protected checkSteps ( ) : boolean {
        let valid = true;
        let firstInvalid: BookingStep = null;
        for ( const step of this.stepViewControllers ) {
            const ok = step.check();
            if ( !ok && firstInvalid == null ) firstInvalid = step;
            valid = ok && valid;
        }

        if ( firstInvalid != null ) {
            // Point the user at the step that needs attention
            this.errorLbl.text = NSLocalizeString( "PLEASE COMPLETE ALL THE STEPS", "Please complete all the steps" );
            this.errorLbl.setHidden( false );
            ( firstInvalid.view?.layer as any )?.scrollIntoView?.( { behavior: "smooth", block: "center" } );
        }

        return valid;
    }

    setupLanguageComboBox() {
        let platformLang = MIOCoreGetPlatformLanguage();
        let langOptions = MIOCoreGetLanguages();

        let i = 0;
        for (let key in langOptions) {
            this.languageComboBox.addItem(key, i);
            i++;
        }
    }

    onLanguageChange = ( ) => {
        //implement change of language here
    }

    onAcceptedCheck = ( ) =>
    {
        (UIApplication.shared.delegate as AppDelegate).dryRunCheck = false;
        if ( DLDB.shared.combineTermsAndPrivacy && this.acceptPrivacyCB ) this.acceptPrivacyCB.on = this.acceptTermsCB.on;
        this.checkBookBtn( ) ;
    }

    checkBookBtn = (  ) =>
    {        
        const bookBtn = this.bookButton ;

       if ( bookBtn ) {
            let v = this.acceptTermsCB.on && this.acceptPrivacyCB.on;            
            if (this.calendarViewController) v = v && this.calendarViewController.check(  );
            if (this.clientViewController) v = v && this.clientViewController.check(  );
            bookBtn.enabled = v;
       }

        const reviewBtn = this.reviewButton ;

        if ( reviewBtn ) {
            // NOTE: kept always enabled (as before) so the user gets the step errors on click
            reviewBtn.enabled = true;
        }
    }


    private onBooking ( )
    {
       if ( this.checkSteps() )
         {
            const cart = DLDB.shared.cart()
                , { comments, ...client } = (this.clientViewController) ? this.clientViewController.asJSON() : { comments: cart.comments }
                , hallComments = DLDB.shared.booking().hallComments
                , countryISO2 = DLDB.shared.booking().countryISO2 ?? ""
                , body: any =            
                  { id: DLDB.shared.booking().id.UUIDString
                  , transactionID: DLDB.shared.booking().transactionID
                  , comments
                  , hallComments
                  , nationalities: countryISO2
                  , client
                  , pax   : cart.effectivePax
                  , zoneID: cart.zoneID ?? undefined
                  , timeID: cart.time
                  , date  : cart.date
                  , language: MIOCoreGetPlatformLanguage()
                  , acceptTerms: this.acceptTermsCB.on
                  , acceptPrivacy: this.acceptPrivacyCB.on
                  , acceptPublicity: this.acceptPublicityCB.on
                  } ;

            if ( cart.eventID ) body.eventID = cart.eventID;
            
            

            this.setLoading( true ) ;
            DLDB.shared.book( body, (code, res) =>
            {
              this.setLoading( false ) ;

              if ( code === 200 && res["error"] == null )
                {
                    const vc = BookingSummaryViewController.newInstance( res.data.identifier != null )
                    this.presentViewController(vc, true);

                    this.reset();
                }
              else 
                {
                   let error = (res.error || "").replace( "[BookingApiError] ", "" );
                   this.errorLbl.text = NSLocalizeString(error, error) ;
                   this.errorLbl.setHidden( false ) ;
                }
            } ) ;
         }

      return false ;
   }

   private onReview ( )
   {
        // From here on the steps show their validation messages
        (UIApplication.shared.delegate as AppDelegate).dryRunCheck = false;

        if ( this.checkSteps() ) {
            this.errorLbl.setHidden( true );
            const vc = new BookingReviewViewController();
            // The review page continues the step numbering of this page (its first step is N+1)
            vc.stepNumberOffset = this.stepViewControllers.length;
            vc.initWithNibName("BookingReviewView");
            this.presentViewController(vc, true);
        }
   }

    setLoading ( loading: boolean )
    {
        this.payLoading.hidden = !loading ;

        const btn = this.bookButton ;
        if ( btn ) btn.enabled = !loading ;
    }

    onNumProductDidChange ( vc: BookingProductViewController, numberOfProducts:number )
    {
        this.highlightCurrentStep( ) ;
        this.checkBookBtn( ) ;
    }

    onNumPeopleDidChange ( vc: BookingCalendarViewController | BookingPaxViewController, numberOfPeople:number )
    {
        this.highlightCurrentStep( ) ;
        this.checkBookBtn( ) ;
    }

    onCalendarDidChange ( vc: BookingCalendarViewController, date:string )
    {
        this.highlightCurrentStep( ) ;
        this.checkBookBtn( ) ;
    }

    onTimeDidChange ( vc: BookingRestaurantViewController, time:string )
    {
        this.highlightCurrentStep( ) ;
        this.checkBookBtn( ) ;
    }

    onClientFormDidChange ( vc: BookingClientViewController )
    {
        this.highlightCurrentStep( ) ;
        this.checkBookBtn( ) ;
    }

    onEventDidChange ( vc: BookingEventViewController, event: any )
    {
        this.highlightCurrentStep( ) ;
        this.checkBookBtn( ) ;
    }

    public clear() {
        this.acceptPrivacyCB.selected = false;
        this.acceptTermsCB.selected = false;
        this.acceptPublicityCB.selected = false;

        this.acceptPrivacyCB.on = false;
        this.acceptTermsCB.on = false;
        this.acceptPublicityCB.on = false;

        this.highlightCurrentStep( ) ;
    }

    public reset() {
        for ( const step of this.stepViewControllers ) step.clear();
        if (this.clientViewController) this.clientViewController.clear();
        this.clear();
    }
}
